// src/oura/merchant/FirstRun.tsx
//
// A new merchant's Home and Visibility before their first AI check is ready.
// Until then both pages would be a wall of dashes and "not checked yet", so
// they say instead what happens next and what each page will show.
//
// Keyed off the First AI check's status on the set-up read, never off empty
// numbers: a missing figure can mean a failed read, and an older backend that
// does not send the status shows the normal pages (absent means unknown).

import type { ReactNode } from "react";
import { CheckIcon, ListChecksIcon, SparklesIcon } from "lucide-react";
import type { ClientPagePayload } from "@service/aeo/clientReview";
import type { MerchantFirstCheck } from "@service/aeo/onboarding";
import { EngineLogo, FoldCard, PageHead, PillLink, SURFACE, TYPE } from "@oura/ui";
import { cn } from "../lib/cn";
import { CLIENT_HINT, CLIENT_HINT_V2, ouraHref } from "../nav";
import { NextCardFrame } from "../overview/NextCardFrame";
import type { FirstRun } from "./firstRunState";
import { ManagerCard } from "./HomeTop";
import { dayMonth } from "../onboarding/derive";
import { REPORTS_PAGE } from "../merchantV2";

const SETUP_HREF = `${ouraHref("decisions", { role: "oura-client" })}?tab=setup`;

const ENGINES = [
    { key: "chatgpt", name: "ChatGPT" },
    { key: "gemini", name: "Gemini" },
    { key: "ai_overview", name: "Google's AI" },
] as const;

/** What the check row says, by where it stands. No dates or countdowns we cannot keep. */
const CHECK_WORD: Record<MerchantFirstCheck["status"], string> = {
    // It can run before the answers are in (Jack, 21 Oct 2026): staff start it.
    after_answers: "Our team starts it",
    preparing: "Our team is preparing it",
    running: "In progress",
    // Finished and with our team: done, as staff's Steps say it (#4443). The row shows the day when known.
    reviewing: "Done",
    ready: "Ready",
};

/** The one thing to know now, as the Next card says it, once setup is done (SetupNeeded speaks before then). */
function nextOf(run: FirstRun): { title: string; why: string; action: ReactNode } {
    if (run.check.status === "running" || run.check.status === "reviewing") {
        return { title: "Your first AI check is in progress", why: `We run it, then our team checks the results. Your Baseline Snapshot and month-by-month plan come to you together, by email and in ${REPORTS_PAGE}.`, action: null };
    }
    return { title: "Our team is preparing your first AI check", why: "Nothing for you to do. We email you the moment your results are ready.", action: null };
}

/** A row on "How Oura starts": a mark, the step, where it stands. */
function StartRow({ state, title, sub, fact }: { state: "done" | "now" | "oura" | "later"; title: string; sub?: string; fact: string }) {
    return (
        <li className="flex min-h-12 items-center gap-3 py-2">
            <span aria-hidden className="flex w-5 shrink-0 justify-center">
                {state === "done" ? (
                    <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white">
                        <CheckIcon size={12} strokeWidth={3} />
                    </span>
                ) : (
                    // Blue is theirs to do now; purple is Oura working, as on Setup.
                    <span className={cn("grid h-5 w-5 place-items-center rounded-full border-2 bg-white", state === "now" ? "border-blue-600" : state === "oura" ? "border-violet-500" : "border-gray-300")}>
                        <span className={cn("h-2 w-2 rounded-full", state === "now" ? "bg-blue-600" : state === "oura" ? "bg-violet-500 motion-safe:animate-pulse" : "bg-transparent")} />
                    </span>
                )}
            </span>
            <span className="min-w-0 flex-1">
                <span className={cn(TYPE.tb, "block", state === "later" ? "text-gray-500" : "text-gray-900")}>{title}</span>
                {sub && <span className={cn(TYPE.tm, "block text-gray-500")}>{sub}</span>}
            </span>
            <span className={cn(TYPE.tc, "shrink-0 text-right tabular-nums text-gray-500")}>{fact}</span>
        </li>
    );
}

/** A greyed preview of something the page will show, with when it arrives. */
function Preview({ title, when, children }: { title: string; when: string; children?: ReactNode }) {
    return (
        <div className="flex min-w-0 flex-col gap-2 rounded-2xl border border-dashed border-gray-200 p-4">
            <p className={cn(TYPE.lab, "m-0")}>{title}</p>
            <div className="flex min-h-8 items-center gap-2 text-gray-300">{children ?? <span className={cn(TYPE.num, "text-gray-300")}>–</span>}</div>
            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{when}</p>
        </div>
    );
}

/** Home before the first AI check: what is next, how Oura starts, who to ask, and what the page will show. */
export function FirstRunHome({ run, payload }: { run: FirstRun; payload: ClientPagePayload }) {
    const next = nextOf(run);
    const setupDone = run.left === 0;
    // Once the check has finished, it is done and the results and plan are what we are working on (#4443).
    const reviewing = run.check.status === "reviewing";
    const checkState = run.check.status === "after_answers" ? "later" : reviewing ? "done" : "oura";
    const checkFact = reviewing ? dayMonth(run.check.finishedAt) || CHECK_WORD.reviewing : CHECK_WORD[run.check.status];
    return (
        <div className="space-y-5">
            {/* While setup is theirs, the setup warning above every page says it (SetupNeeded): no second card for it. */}
            {setupDone && <NextCardFrame complete={false} tag={{ label: "With us", tone: "blue" }} title={next.title} why={next.why} action={next.action} percent={null} progressLabel="" inlineTag />}

            <FoldCard id="first-run-start" title="How Oura starts" icon={{ icon: ListChecksIcon, area: "work" }} foldedFact={setupDone ? "Setup done" : `${run.total - run.left} of ${run.total}`} alwaysOpen>
                    <ol className="list-plain m-0 divide-y divide-gray-100 p-0">
                        <StartRow state={setupDone ? "done" : "now"} title="Your setup" sub="About 8 minutes, saved as you go" fact={setupDone ? "Done" : `${run.total - run.left} of ${run.total} done`} />
                        <StartRow state={checkState} title="First AI check" sub="By Oura. We ask ChatGPT, Gemini and Google's AI 50 questions your customers ask" fact={checkFact} />
                        <StartRow state={reviewing ? "oura" : "later"} title="Your results and plan" sub="Your Baseline Snapshot and month-by-month plan, sent together by email" fact={reviewing ? "In progress" : "After the check"} />
                    </ol>
                </FoldCard>

            <ManagerCard payload={payload} />

            <section aria-labelledby="first-run-preview" className={cn(SURFACE.card, "flex flex-col gap-4 p-6")}>
                <div>
                    <h2 id="first-run-preview" className={cn(TYPE.tt, "m-0 text-gray-900")}>
                        What you will see here
                    </h2>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>Everything below is measured from your first AI check.</p>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <Preview title="How often AI names you" when="After your first AI check" />
                    <Preview title="Each AI assistant" when="After your first AI check">
                        {ENGINES.map((e) => (
                            <span key={e.key} className="opacity-40 grayscale">
                                <EngineLogo engine={e.key} size={20} label={e.name} />
                            </span>
                        ))}
                    </Preview>
                    <Preview title="This month's work" when="Once your plan starts" />
                </div>
            </section>
        </div>
    );
}

/** Visibility before the first AI check: what the page will show, and where the check stands. */
export function FirstCheckBlank({ run }: { run: FirstRun }) {
    const setupLeft = run.left > 0;
    return (
        <>
            <PageHead title={CLIENT_HINT_V2.questions} sentence={CLIENT_HINT.questions} />
            <section className={cn(SURFACE.card, "flex flex-col items-start gap-5 p-6 sm:p-8")}>
                <span aria-hidden className="grid h-12 w-12 place-items-center rounded-full bg-blue-50 text-blue-600">
                    <SparklesIcon size={22} />
                </span>
                <div className="max-w-xl">
                    <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>
                        {run.check.status === "after_answers" ? "Your first AI check hasn't started yet" : run.check.status === "reviewing" || run.check.status === "running" ? "Your first AI check is in progress" : "Your first AI check is on its way"}
                    </h2>
                    <p className={cn(TYPE.tb, "m-0 mt-1.5 text-gray-600")}>
                        Here you will see, for each of the 50 questions your customers ask, whether ChatGPT, Gemini and Google's AI name you, and who they name instead.
                    </p>
                </div>
                <ul className="list-plain m-0 grid w-full grid-cols-1 gap-3 p-0 sm:grid-cols-3">
                    {ENGINES.map((e) => (
                        <li key={e.key} className="flex items-center gap-3 rounded-2xl border border-dashed border-gray-200 px-4 py-3">
                            <span className="opacity-40 grayscale">
                                <EngineLogo engine={e.key} size={20} />
                            </span>
                            <span className="min-w-0">
                                <span className={cn(TYPE.tb, "block text-gray-900")}>{e.name}</span>
                                <span className={cn(TYPE.tc, "block text-gray-500")}>{run.check.status === "after_answers" ? "Not checked yet" : "Results come with your plan"}</span>
                            </span>
                        </li>
                    ))}
                </ul>
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                    {run.check.status === "after_answers"
                        ? "Our team starts it. We email you when your results are ready."
                        : run.check.status === "reviewing" || run.check.status === "running"
                          ? "In progress. We email you your results and plan together."
                            : "Our team is preparing the 50 questions, then starts it. We email you when your results are ready."}
                </p>
                {setupLeft && (
                    <PillLink variant="primary" to={SETUP_HREF} className="max-sm:h-9">
                        Continue setup
                    </PillLink>
                )}
            </section>
        </>
    );
}
